import { calculateTemporaryPriceLines, normalizeTemporaryPrice } from './temporaryPriceCore';
import { isScaleInputPrice } from './scalePresentation';
import { computeFormatOfferSubtotal, getFormatOfferId, isFormatOfferPrice } from './formatOffers';
import { formatPromotionTerms, getPromotionState } from './priceListPromotions';

const PROMOTION_TYPES = ['unit_price', 'temporary_price', 'pack', 'weight_threshold'];
const MAX_PROMOTION_FRONTIER_STATES = 262144;
const MAX_PROMOTION_DP_TRANSITIONS = 5000000;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toInteger = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

const normalizeIdList = (value) => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(
    value
      .map((entry) => toInteger(entry))
      .filter((entry) => Number.isInteger(entry) && entry > 0)
  ));
};

export const resolvePromotionQuantityType = (metricUnit, quantity) => {
  const normalizedUnit = String(metricUnit || '').toLowerCase().trim();
  const qty = toNumber(quantity);
  const hasDecimals = Number.isFinite(qty) && Math.floor(qty) !== qty;
  if (normalizedUnit.includes('kg') || normalizedUnit.includes('kilo') || normalizedUnit.includes('gr')) {
    return 'weight';
  }
  if (normalizedUnit === 'g' || normalizedUnit === 'gr') {
    return 'weight';
  }
  if (normalizedUnit.includes('lt') || normalizedUnit.includes('litro') || normalizedUnit === 'l') {
    return 'other';
  }
  if (hasDecimals) return 'weight';
  return 'unit';
};

export const buildPromotionLineItem = ({
  product,
  variety,
  price,
  priceIndex,
  quantity,
  discount = 0,
  extra = {},
}) => ({
  varietyId: variety?.id,
  varietyPriceId: price?.parentVarietyPriceId ?? price?.id,
  ...(isFormatOfferPrice(price) ? { formatOfferId: getFormatOfferId(price) } : {}),
  varietyName: variety?.varietyName || null,
  productName: product?.productName || null,
  productImage: product?.productImage || null,
  varietyImage: variety?.varietyImage || null,
  priceImage: price?.priceImage || null,
  description: price?.description || null,
  priceDescription: price?.description || null,
  categoryId: product?.categoryId ?? null,
  categoryName: product?.Category?.name || null,
  metricUnit: price?.metricUnit || null,
  ...(price?.useScaleWeight ? { useScaleWeight: true } : {}),
  price: Number(price?.price || 0),
  isSoldOut: Boolean(price?.isSoldOut || variety?.isSoldOut),
  regularPrice: Number(price?.regularPrice ?? price?.price ?? 0),
  ...(price?.priceSnapshot ? { priceSnapshot: price.priceSnapshot, priceSnapshotAt: price.posPriceContext?.now } : {}),
  ...(price?.posPriceContext ? { posPriceContext: price.posPriceContext } : {}),
  discount: isFormatOfferPrice(price) ? 0 : Number(discount || 0),
  quantity: Number(quantity || 0),
  quantityType: isScaleInputPrice(price)
    ? 'weight'
    : resolvePromotionQuantityType(price?.metricUnit, quantity),
  priceIndex,
  ...extra,
  ...(price?.posClientPricing || {}),
  ...(isFormatOfferPrice(price) ? {
    autoPriceSelection: false,
    priceTypeOverride: false,
    discount: 0,
    discountSource: 'format_offer',
    promotionType: 'format_offer',
    promotionName: 'Oferta',
    promotionId: null,
    promotionDiscountAmount: 0,
    promotionSnapshot: price.promotionSnapshot?.kind === 'format_offer'
      ? price.promotionSnapshot
      : { ...price.formatOffer, kind: 'format_offer', formatOfferId: getFormatOfferId(price), varietyPriceId: price.parentVarietyPriceId },
  } : {}),
});

const normalizeRuntimePromotion = (promotion = {}) => {
  const config = promotion?.config || {};
  const rawType = String(promotion?.type || '').trim().toLowerCase();
  const type = rawType === 'temporary_price' ? 'unit_price' : rawType;
  const directTarget = toInteger(promotion?.varietyPriceId);
  const legacyTargets = normalizeIdList(config.varietyPriceIds);
  const varietyPriceIds = directTarget ? [directTarget] : legacyTargets;
  const promotionPrice = toInteger(
    promotion?.promotionPrice
      ?? promotion?.sellingPrice
      ?? (type === 'pack' ? config.packPrice : config.promoUnitPrice)
  );
  const quantity = type === 'weight_threshold' ? Number(promotion.quantity) : type === 'pack'
    ? toInteger(promotion?.quantity ?? config.packQuantity)
    : 1;
  const normalized = {
    ...promotion,
    type,
    quantity,
    promotionPrice,
    varietyPriceIds,
    startsAt: promotion?.startsAt || null,
    expiresAt: promotion?.expiresAt || promotion?.endsAt || null,
  };
  return {
    ...normalized,
    name: String(promotion?.name || '').trim() || formatPromotionTerms(normalized),
  };
};

const isPromotionActiveForContext = (promotion, context, now = Date.now()) => {
  if (!promotion || promotion.isDeleted) return false;
  const normalized = normalizeRuntimePromotion(promotion);
  if (!PROMOTION_TYPES.includes(normalized.type)) return false;
  const status = String(promotion.status || 'active').toLowerCase();
  if (status !== 'active') return false;
  if (context === 'quotation' && promotion.appliesToQuotations === false) return false;
  if (context !== 'quotation' && promotion.appliesToSales === false) return false;
  if (promotion.endedAt || promotion.endedReason) return false;
  return getPromotionState(normalized, now) === 'active';
};

const computeBaseSubtotal = (item) => {
  if (getFormatOfferId(item)) return computeFormatOfferSubtotal(item?.price, item?.quantity);
  const price = toNumber(item?.price) || 0;
  const quantity = toNumber(item?.quantity) || 0;
  if (quantity <= 0) return 0;
  return Math.max(Math.floor(price * quantity), 0);
};

const computeManualSubtotal = (item) => {
  if (getFormatOfferId(item)) return computeFormatOfferSubtotal(item?.price, item?.quantity);
  const price = toNumber(item?.price) || 0;
  const source = String(item?.discountSource || '').trim().toLowerCase();
  const manualDiscount = toNumber(item?.manualDiscount) ?? toNumber(item?.discount) ?? 0;
  const discount = Math.max(
    source === 'promotion'
      ? manualDiscount
      : toNumber(item?.discount) ?? manualDiscount,
    0
  );
  const quantity = toNumber(item?.quantity) || 0;
  if (quantity <= 0) return 0;
  return Math.max(Math.floor((price - discount) * quantity), 0);
};

const isUnitIntegerQuantity = (item) => {
  const quantity = toNumber(item?.quantity);
  if (!Number.isInteger(quantity) || quantity <= 0) return false;
  const quantityType = item?.quantityType
    || resolvePromotionQuantityType(item?.metricUnit, quantity);
  return String(quantityType || 'unit').trim().toLowerCase() === 'unit';
};

const buildPromotionSnapshot = (promotion, pricing) => ({
  kind: 'price_list_promotion',
  id: promotion.id,
  batchId: promotion.batchId || null,
  varietyPriceId: promotion.varietyPriceIds?.[0] || null,
  name: promotion.name,
  type: promotion.type,
  quantity: promotion.quantity,
  promotionPrice: promotion.promotionPrice,
  startsAt: promotion.startsAt || null,
  expiresAt: promotion.expiresAt || null,
  startDate: promotion.startDate || null,
  endDate: promotion.endDate || null,
  promotedQuantity: pricing?.promotedQuantity ?? null,
  remainderQuantity: pricing?.remainderQuantity ?? null,
  protectedSubtotal: pricing?.protectedSubtotal ?? null,
  eligibleSubtotal: pricing?.eligibleSubtotal ?? null,
  config: promotion.config || {},
});

const computePromotionPricing = (item, rawPromotion, ordinaryUnitPrice) => {
  const promotion = normalizeRuntimePromotion(rawPromotion);
  const varietyPriceId = toInteger(item?.varietyPriceId);
  if (!Number.isInteger(varietyPriceId) || !promotion.varietyPriceIds.includes(varietyPriceId)) return null;

  const price = toNumber(item?.price) || 0;
  const quantity = toNumber(item?.quantity) || 0;
  if (price <= 0 || quantity <= 0) return null;

  if (promotion.type === 'unit_price') {
    const promoUnitPrice = promotion.promotionPrice;
    if (!Number.isInteger(promoUnitPrice) || promoUnitPrice <= 0) return null;
    const subtotal = Math.max(Math.floor(promoUnitPrice * quantity), 0);
    return {
      promotion,
      subtotal,
      protectedSubtotal: subtotal,
      eligibleSubtotal: 0,
      promotedQuantity: quantity,
      remainderQuantity: 0,
    };
  }

  if (promotion.type === 'pack') {
    if (!isUnitIntegerQuantity(item)) return null;
    const packQuantity = promotion.quantity;
    const packPrice = promotion.promotionPrice;
    if (!Number.isInteger(packQuantity) || packQuantity <= 1) return null;
    if (!Number.isInteger(packPrice) || packPrice <= 0) return null;
    const packs = Math.floor(quantity / packQuantity);
    const remainder = quantity % packQuantity;
    if (packs <= 0) return null;
    const protectedSubtotal = Math.max(Math.floor(packs * packPrice), 0);
    const eligibleSubtotal = Math.max(Math.floor(remainder * ordinaryUnitPrice), 0);
    return {
      promotion,
      subtotal: protectedSubtotal + eligibleSubtotal,
      protectedSubtotal,
      eligibleSubtotal,
      promotedQuantity: packs * packQuantity,
      remainderQuantity: remainder,
    };
  }

  return null;
};

const resolveOrderDiscountPercentage = (pricingOptions = {}) => {
  const explicitPercentage = pricingOptions.orderDiscountPercentage != null
    ? Number(pricingOptions.orderDiscountPercentage)
    : null;
  if (Number.isFinite(explicitPercentage)) {
    return Math.min(Math.max(explicitPercentage, 0), 100);
  }
  const explicitRate = pricingOptions.orderDiscountRate != null
    ? Number(pricingOptions.orderDiscountRate)
    : null;
  return Number.isFinite(explicitRate)
    ? Math.min(Math.max(explicitRate * 100, 0), 100)
    : null;
};

const resolveOrderDiscountAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(Math.floor(parsed), 0) : 0;
};

const computeOptionsTotal = ({ subtotal, eligibleSubtotal, protectedSubtotal }, pricingOptions = {}) => {
  const protectedAmount = Math.max(Number(protectedSubtotal || 0), 0);
  const resolvedSubtotal = Math.max(Number(subtotal ?? 0), 0);
  const eligible = eligibleSubtotal == null
    ? Math.max(resolvedSubtotal - protectedAmount, 0)
    : Math.max(Number(eligibleSubtotal || 0), 0);
  const percent = resolveOrderDiscountPercentage(pricingOptions);
  if (percent !== null) {
    return protectedAmount
      + Math.max(eligible - Math.floor(eligible * (percent / 100)), 0);
  }
  const amount = resolveOrderDiscountAmount(pricingOptions.orderDiscountAmount);
  return protectedAmount + Math.max(eligible - amount, 0);
};

const positivePromotionId = (value) => {
  const parsed = toInteger(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const comparePromotionIds = (left, right) => (
  positivePromotionId(left) - positivePromotionId(right)
);

const compareStatePreference = (left, right) => (
  left.promotionCount - right.promotionCount
  || left.choiceRank - right.choiceRank
);

const compareUnrankedStatePreference = (left, right) => (
  left.promotionCount - right.promotionCount
  || left.previousChoiceRank - right.previousChoiceRank
  || comparePromotionIds(left.choiceId, right.choiceId)
);

const pruneDominatedPricingStates = (states) => {
  const bestByEligible = new Map();
  states.forEach((state) => {
    const eligibleSubtotal = state.subtotal - state.protectedSubtotal;
    const current = bestByEligible.get(eligibleSubtotal);
    if (!current
      || state.protectedSubtotal < current.protectedSubtotal
      || (state.protectedSubtotal === current.protectedSubtotal
        && compareStatePreference(state, current) < 0)) {
      bestByEligible.set(eligibleSubtotal, state);
    }
  });
  const sortedStates = [...bestByEligible.values()].sort((left, right) => (
    left.protectedSubtotal - right.protectedSubtotal
    || left.subtotal - right.subtotal
    || compareStatePreference(left, right)
  ));
  const subtotalValues = Array.from(new Set(
    sortedStates.map((state) => state.subtotal)
  )).sort((left, right) => left - right);
  const subtotalIndex = new Map(
    subtotalValues.map((subtotal, index) => [subtotal, index + 1])
  );
  const preferenceTree = Array(subtotalValues.length + 1).fill(null);
  const queryPreferredState = (endIndex) => {
    let winner = null;
    for (let index = endIndex; index > 0; index -= index & -index) {
      const candidate = preferenceTree[index];
      if (candidate && (!winner || compareStatePreference(candidate, winner) < 0)) {
        winner = candidate;
      }
    }
    return winner;
  };
  const recordState = (startIndex, state) => {
    for (let index = startIndex; index < preferenceTree.length; index += index & -index) {
      if (!preferenceTree[index]
        || compareStatePreference(state, preferenceTree[index]) < 0) {
        preferenceTree[index] = state;
      }
    }
  };
  const frontier = [];
  sortedStates.forEach((state) => {
    const index = subtotalIndex.get(state.subtotal);
    const economicDominator = queryPreferredState(index);
    if (economicDominator
      && compareStatePreference(economicDominator, state) <= 0) return;
    frontier.push(state);
    recordState(index, state);
  });
  return frontier;
};

const pruneDominatedLineOptions = (options) => {
  const candidates = options.map((option) => ({
    option,
    subtotal: option.eligibleSubtotal + option.protectedSubtotal,
    protectedSubtotal: option.protectedSubtotal,
    promotionCount: option.isPromotion ? 1 : 0,
    choiceRank: positivePromotionId(option.choiceId),
  }));
  return pruneDominatedPricingStates(candidates).map((candidate) => candidate.option);
};

const assignChoiceRanks = (states) => {
  const pairsByKey = new Map();
  states.forEach((state) => {
    const key = `${state.previousChoiceRank}:${state.choiceId}`;
    if (!pairsByKey.has(key)) {
      pairsByKey.set(key, {
        previousChoiceRank: state.previousChoiceRank,
        choiceId: state.choiceId,
      });
    }
  });
  const sortedPairs = Array.from(pairsByKey.entries()).sort((left, right) => (
    left[1].previousChoiceRank - right[1].previousChoiceRank
    || comparePromotionIds(left[1].choiceId, right[1].choiceId)
  ));
  const rankByKey = new Map();
  sortedPairs.forEach(([key], index) => rankByKey.set(key, index));
  states.forEach((state) => {
    state.choiceRank = rankByKey.get(`${state.previousChoiceRank}:${state.choiceId}`);
  });
  return states;
};

const expandPricingStates = (states, lineOptions) => {
  const expandedStates = new Array(states.length * lineOptions.length);
  let cursor = 0;
  states.forEach((state) => {
    lineOptions.forEach((option, optionIndex) => {
      expandedStates[cursor] = {
        protectedSubtotal: state.protectedSubtotal + option.protectedSubtotal,
        subtotal: state.subtotal + option.eligibleSubtotal + option.protectedSubtotal,
        promotionCount: state.promotionCount + (option.isPromotion ? 1 : 0),
        previousChoiceRank: state.choiceRank,
        choiceId: positivePromotionId(option.choiceId),
        choiceRank: 0,
        node: { previous: state.node, optionIndex },
      };
      cursor += 1;
    });
  });
  return assignChoiceRanks(expandedStates);
};

const enumeratePricingFrontier = (optionsByLine) => {
  let states = [{
    protectedSubtotal: 0,
    subtotal: 0,
    promotionCount: 0,
    choiceRank: 0,
    node: null,
  }];
  optionsByLine.forEach((lineOptions) => {
    states = pruneDominatedPricingStates(expandPricingStates(states, lineOptions));
  });
  return states;
};

const findBalancedSplitIndex = (optionsByLine) => {
  if (optionsByLine.length <= 1) return Math.floor(optionsByLine.length / 2);
  const weights = optionsByLine.map((options) => Math.log(Math.max(options.length, 1)));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (totalWeight === 0) return Math.floor(optionsByLine.length / 2);
  let prefixWeight = 0;
  let bestIndex = 0;
  let bestDifference = Infinity;
  for (let index = 0; index <= optionsByLine.length; index += 1) {
    const difference = Math.abs(totalWeight - (2 * prefixWeight));
    if (difference < bestDifference) {
      bestDifference = difference;
      bestIndex = index;
    }
    prefixWeight += weights[index] || 0;
  }
  return bestIndex;
};

const compareCombinedPreference = (left, right) => {
  const promotionCountComparison = (
    left.left.promotionCount + left.right.promotionCount
  ) - (
    right.left.promotionCount + right.right.promotionCount
  );
  if (promotionCountComparison !== 0) return promotionCountComparison;
  return left.left.choiceRank - right.left.choiceRank
    || left.right.choiceRank - right.right.choiceRank;
};

const reconstructOptionIndexes = (node, length) => {
  const indexes = Array(length).fill(0);
  let currentNode = node;
  for (let index = length - 1; index >= 0; index -= 1) {
    indexes[index] = currentNode?.optionIndex ?? 0;
    currentNode = currentNode?.previous || null;
  }
  return indexes;
};

const buildMeetInMiddleFrontiers = (optionsByLine) => {
  const splitIndex = findBalancedSplitIndex(optionsByLine);
  return {
    splitIndex,
    left: enumeratePricingFrontier(optionsByLine.slice(0, splitIndex)),
    right: enumeratePricingFrontier(optionsByLine.slice(splitIndex)),
  };
};

const estimateCombinationCount = (optionsByLine, limit = MAX_PROMOTION_FRONTIER_STATES) => {
  let total = 1;
  for (const options of optionsByLine) {
    total *= Math.max(options.length, 1);
    if (total > limit) return limit + 1;
  }
  return total;
};

const canEnumerateMeetInMiddle = (optionsByLine) => {
  const splitIndex = findBalancedSplitIndex(optionsByLine);
  return estimateCombinationCount(optionsByLine.slice(0, splitIndex))
    <= MAX_PROMOTION_FRONTIER_STATES
    && estimateCombinationCount(optionsByLine.slice(splitIndex))
      <= MAX_PROMOTION_FRONTIER_STATES;
};

const chooseIndependentOptions = (optionsByLine, metric) => optionsByLine.map((options) => {
  let winnerIndex = 0;
  options.forEach((option, optionIndex) => {
    const winner = options[winnerIndex];
    const comparison = metric(option) - metric(winner)
      || Number(option.isPromotion) - Number(winner.isPromotion)
      || comparePromotionIds(option.choiceId, winner.choiceId);
    if (comparison < 0) winnerIndex = optionIndex;
  });
  return winnerIndex;
});

const chooseBestStateByMetric = (current, candidate, metric) => {
  if (!current) return candidate;
  if (candidate[metric] !== current[metric]) {
    return candidate[metric] < current[metric] ? candidate : current;
  }
  return compareStatePreference(candidate, current) < 0 ? candidate : current;
};

const chooseFixedDiscountOptions = (optionsByLine, orderDiscountAmount) => {
  const { splitIndex, left, right } = buildMeetInMiddleFrontiers(optionsByLine);
  const discountAmount = resolveOrderDiscountAmount(orderDiscountAmount);
  const sortedRight = [...right].sort((first, second) => (
    (first.subtotal - first.protectedSubtotal) - (second.subtotal - second.protectedSubtotal)
    || compareStatePreference(first, second)
  ));
  const prefixBestProtected = Array(sortedRight.length);
  const suffixBestSubtotal = Array(sortedRight.length);
  sortedRight.forEach((state, index) => {
    prefixBestProtected[index] = chooseBestStateByMetric(
      index > 0 ? prefixBestProtected[index - 1] : null,
      state,
      'protectedSubtotal'
    );
  });
  for (let index = sortedRight.length - 1; index >= 0; index -= 1) {
    suffixBestSubtotal[index] = chooseBestStateByMetric(
      index + 1 < sortedRight.length ? suffixBestSubtotal[index + 1] : null,
      sortedRight[index],
      'subtotal'
    );
  }

  let winner = null;
  let winningTotal = Infinity;
  const consider = (leftState, rightState) => {
    if (!rightState) return;
    const combined = { left: leftState, right: rightState };
    const subtotal = leftState.subtotal + rightState.subtotal;
    const protectedSubtotal = leftState.protectedSubtotal + rightState.protectedSubtotal;
    const total = protectedSubtotal + Math.max(
      subtotal - protectedSubtotal - discountAmount,
      0
    );
    if (total < winningTotal || (
      total === winningTotal
      && (!winner || compareCombinedPreference(combined, winner) < 0)
    )) {
      winner = combined;
      winningTotal = total;
    }
  };

  left.forEach((leftState) => {
    const minimumRightEligible = discountAmount
      - (leftState.subtotal - leftState.protectedSubtotal);
    let low = 0;
    let high = sortedRight.length;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      const eligible = sortedRight[middle].subtotal - sortedRight[middle].protectedSubtotal;
      if (eligible < minimumRightEligible) low = middle + 1;
      else high = middle;
    }
    if (low > 0) consider(leftState, prefixBestProtected[low - 1]);
    if (low < sortedRight.length) consider(leftState, suffixBestSubtotal[low]);
  });

  return [
    ...reconstructOptionIndexes(winner?.left?.node, splitIndex),
    ...reconstructOptionIndexes(winner?.right?.node, optionsByLine.length - splitIndex),
  ];
};

const chooseFixedDiscountOptionsWithSparseDp = (optionsByLine, orderDiscountAmount) => {
  const discountAmount = resolveOrderDiscountAmount(orderDiscountAmount);
  let transitionCount = 0;
  let unsaturated = new Map([[
    0,
    {
      eligibleSubtotal: 0,
      protectedSubtotal: 0,
      subtotal: 0,
      promotionCount: 0,
      choiceRank: 0,
      node: null,
    },
  ]]);
  let saturated = null;

  for (const lineOptions of optionsByLine) {
    transitionCount += (unsaturated.size + Number(Boolean(saturated))) * lineOptions.length;
    if (transitionCount > MAX_PROMOTION_DP_TRANSITIONS) return null;
    const nextUnsaturated = new Map();
    let nextSaturated = null;
    const consider = (state, option, optionIndex) => {
      const eligibleSubtotal = state.eligibleSubtotal + option.eligibleSubtotal;
      const protectedSubtotal = state.protectedSubtotal + option.protectedSubtotal;
      const subtotal = eligibleSubtotal + protectedSubtotal;
      const candidate = {
        eligibleSubtotal,
        protectedSubtotal,
        subtotal,
        promotionCount: state.promotionCount + (option.isPromotion ? 1 : 0),
        previousChoiceRank: state.choiceRank,
        choiceId: positivePromotionId(option.choiceId),
        choiceRank: 0,
        node: { previous: state.node, optionIndex },
      };
      if (eligibleSubtotal >= discountAmount) {
        if (!nextSaturated
          || candidate.subtotal < nextSaturated.subtotal
          || (candidate.subtotal === nextSaturated.subtotal
            && compareUnrankedStatePreference(candidate, nextSaturated) < 0)) {
          nextSaturated = candidate;
        }
        return;
      }
      const current = nextUnsaturated.get(eligibleSubtotal);
      if (!current
        || candidate.protectedSubtotal < current.protectedSubtotal
        || (candidate.protectedSubtotal === current.protectedSubtotal
          && compareUnrankedStatePreference(candidate, current) < 0)) {
        nextUnsaturated.set(eligibleSubtotal, candidate);
      }
    };

    unsaturated.forEach((state) => {
      lineOptions.forEach((option, optionIndex) => consider(state, option, optionIndex));
    });
    if (saturated) {
      lineOptions.forEach((option, optionIndex) => consider(saturated, option, optionIndex));
    }
    if (nextUnsaturated.size + Number(Boolean(nextSaturated)) > MAX_PROMOTION_FRONTIER_STATES) {
      return null;
    }
    const survivors = [...nextUnsaturated.values(), ...(nextSaturated ? [nextSaturated] : [])];
    assignChoiceRanks(survivors);
    unsaturated = new Map(
      survivors
        .filter((state) => state.eligibleSubtotal < discountAmount)
        .map((state) => [state.eligibleSubtotal, state])
    );
    saturated = survivors.find((state) => state.eligibleSubtotal >= discountAmount) || null;
  }

  let winner = saturated;
  let winningTotal = saturated ? saturated.subtotal - discountAmount : Infinity;
  unsaturated.forEach((state) => {
    const total = state.protectedSubtotal;
    if (total < winningTotal || (
      total === winningTotal
      && (!winner || compareStatePreference(state, winner) < 0)
    )) {
      winner = state;
      winningTotal = total;
    }
  });
  return reconstructOptionIndexes(winner?.node, optionsByLine.length);
};

const greatestCommonDivisor = (left, right) => {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};

const resolvePercentageRateFraction = (percentage) => {
  if (!Number.isInteger(percentage)) return null;
  const divisor = greatestCommonDivisor(percentage, 100);
  return { numerator: percentage / divisor, denominator: 100 / divisor };
};

const choosePercentageDiscountOptions = (optionsByLine, percentage) => {
  const fraction = resolvePercentageRateFraction(percentage);
  if (!fraction) return null;
  const { numerator, denominator } = fraction;
  let states = [{
    discountedTotal: 0,
    remainder: 0,
    promotionCount: 0,
    choiceRank: 0,
    node: null,
  }];

  optionsByLine.forEach((lineOptions) => {
    const expandedStates = [];
    states.forEach((state) => {
      lineOptions.forEach((option, optionIndex) => {
        const subtotal = option.eligibleSubtotal + option.protectedSubtotal;
        const discountProduct = option.eligibleSubtotal * numerator;
        const optionRemainder = discountProduct % denominator;
        const combinedRemainder = state.remainder + optionRemainder;
        expandedStates.push({
          discountedTotal: state.discountedTotal
            + subtotal
            - Math.floor(discountProduct / denominator)
            - Math.floor(combinedRemainder / denominator),
          remainder: combinedRemainder % denominator,
          promotionCount: state.promotionCount + (option.isPromotion ? 1 : 0),
          previousChoiceRank: state.choiceRank,
          choiceId: positivePromotionId(option.choiceId),
          choiceRank: 0,
          node: { previous: state.node, optionIndex },
        });
      });
    });
    assignChoiceRanks(expandedStates);
    const bestByRemainder = new Map();
    expandedStates.forEach((candidate) => {
      const current = bestByRemainder.get(candidate.remainder);
      if (!current
        || candidate.discountedTotal < current.discountedTotal
        || (candidate.discountedTotal === current.discountedTotal
          && compareStatePreference(candidate, current) < 0)) {
        bestByRemainder.set(candidate.remainder, candidate);
      }
    });
    states = Array.from(bestByRemainder.values());
  });

  const winner = states.reduce((best, state) => {
    if (!best || state.discountedTotal < best.discountedTotal) return state;
    if (state.discountedTotal > best.discountedTotal) return best;
    return compareStatePreference(state, best) < 0 ? state : best;
  }, null);
  return reconstructOptionIndexes(winner?.node, optionsByLine.length);
};

const chooseGenericDiscountOptions = (optionsByLine, pricingOptions) => {
  const { splitIndex, left, right } = buildMeetInMiddleFrontiers(optionsByLine);
  let winner = null;
  let winningTotal = Infinity;
  left.forEach((leftState) => {
    right.forEach((rightState) => {
      const combined = { left: leftState, right: rightState };
      const total = computeOptionsTotal({
        subtotal: leftState.subtotal + rightState.subtotal,
        protectedSubtotal: leftState.protectedSubtotal + rightState.protectedSubtotal,
      }, pricingOptions);
      if (total < winningTotal || (
        total === winningTotal
        && (!winner || compareCombinedPreference(combined, winner) < 0)
      )) {
        winner = combined;
        winningTotal = total;
      }
    });
  });
  return [
    ...reconstructOptionIndexes(winner?.left?.node, splitIndex),
    ...reconstructOptionIndexes(winner?.right?.node, optionsByLine.length - splitIndex),
  ];
};

const chooseGloballyCheapestOptions = (optionsByLine, pricingOptions) => {
  if (!optionsByLine.length) return [];
  const percentage = resolveOrderDiscountPercentage(pricingOptions);
  if (percentage !== null) {
    return choosePercentageDiscountOptions(optionsByLine, percentage)
      || chooseGenericDiscountOptions(optionsByLine, pricingOptions);
  }
  const discountAmount = resolveOrderDiscountAmount(pricingOptions.orderDiscountAmount);
  if (discountAmount <= 0) {
    return chooseIndependentOptions(
      optionsByLine,
      (option) => option.eligibleSubtotal + option.protectedSubtotal
    );
  }
  const maximumEligibleSubtotal = optionsByLine.reduce((sum, options) => (
    sum + Math.max(...options.map((option) => option.eligibleSubtotal))
  ), 0);
  if (discountAmount >= maximumEligibleSubtotal) {
    return chooseIndependentOptions(optionsByLine, (option) => option.protectedSubtotal);
  }
  if (!canEnumerateMeetInMiddle(optionsByLine)) {
    const selected = chooseFixedDiscountOptionsWithSparseDp(optionsByLine, discountAmount);
    if (selected) return selected;
    const error = new Error('No se pudieron evaluar todas las combinaciones de promociones.');
    error.code = 'PROMOTION_EVALUATION_LIMIT';
    throw error;
  }
  return chooseFixedDiscountOptions(optionsByLine, discountAmount);
};

const resolveLineTaxProfile = (pricingOptions, item, index) => {
  if (typeof pricingOptions.resolveTaxProfile === 'function') {
    return pricingOptions.resolveTaxProfile(item, index) || null;
  }
  if (Array.isArray(pricingOptions.taxProfiles)) {
    return pricingOptions.taxProfiles[index] || null;
  }
  const profiles = pricingOptions.taxProfileByVarietyId;
  if (profiles instanceof Map) {
    return profiles.get(Number(item?.varietyId)) || null;
  }
  if (profiles && typeof profiles === 'object') {
    return profiles[item?.varietyId] || profiles[String(item?.varietyId)] || null;
  }
  return null;
};

const adjustGrossForTaxProfile = (gross, profile) => {
  const safeGross = Math.max(0, Math.round(Number(gross) || 0));
  const shouldAdjust = profile?.adjustForExclusions === true
    || profile?.appliesTaxExclusion === true;
  const fullRate = Number(profile?.fullRate);
  const remainingRate = Number(profile?.remainingRate);
  if (!shouldAdjust
    || !Number.isFinite(fullRate)
    || !Number.isFinite(remainingRate)
    || fullRate <= 0) {
    return safeGross;
  }
  const netFromFull = Math.round(safeGross / (1 + fullRate));
  return Math.max(0, Math.round(netFromFull * (1 + remainingRate)));
};

const withTaxAdjustedOptimizationCoordinates = (option, profile) => {
  const rawSubtotal = option.eligibleSubtotal + option.protectedSubtotal;
  const subtotal = adjustGrossForTaxProfile(rawSubtotal, profile);
  const eligibleSubtotal = Math.min(
    adjustGrossForTaxProfile(option.eligibleSubtotal, profile),
    subtotal
  );
  return {
    ...option,
    eligibleSubtotal,
    protectedSubtotal: subtotal - eligibleSubtotal,
  };
};

export const applyPromotionsToLineItems = (
  items = [],
  promotions = [],
  context = 'sale',
  pricingOptions = {}
) => {
  const safeItems = Array.isArray(items) ? items : [];
  const now = pricingOptions.now ?? Date.now();
  const activePromotions = (Array.isArray(promotions) ? promotions : [])
    .filter((promotion) => isPromotionActiveForContext(promotion, context, now))
    .map(normalizeRuntimePromotion);

  if ((promotions || []).some((row) => normalizeTemporaryPrice(row))
    || safeItems.some((item) => item.posPriceContext || item.promotionSnapshot?.kind === 'temporary_price')) {
    return calculateTemporaryPriceLines(safeItems.map((item) => ({ item, skip: Boolean(getFormatOfferId(item)),
      records: item.posPriceContext?.promotions || promotions, now: item.posPriceContext?.now || now })));
  }
  const optionsByLine = safeItems.map((item, itemIndex) => {
    const taxProfile = resolveLineTaxProfile(pricingOptions, item, itemIndex);
    const baseSubtotal = computeBaseSubtotal(item);
    if (getFormatOfferId(item)) {
      const result = {
        ...item,
        formatOfferId: getFormatOfferId(item),
        discount: 0,
        manualDiscount: 0,
        discountSource: 'format_offer',
        promotionType: 'format_offer',
        promotionName: 'Oferta',
        promotionDiscountAmount: 0,
        promotionId: null,
        subtotal: baseSubtotal,
        orderDiscountEligibleSubtotal: 0,
        promotionProtectedSubtotal: baseSubtotal,
      };
      return [withTaxAdjustedOptimizationCoordinates({
        eligibleSubtotal: 0,
        protectedSubtotal: baseSubtotal,
        isPromotion: false,
        choiceId: 0,
        result,
      }, taxProfile)];
    }
    const manualDiscount = Math.max(
      toNumber(item?.manualDiscount) ?? toNumber(item?.discount) ?? 0,
      0
    );
    const sourceBeforePromotion = String(item?.discountSource || '').trim().toLowerCase();
    const effectiveDiscount = Math.max(
      sourceBeforePromotion === 'promotion'
        ? manualDiscount
        : toNumber(item?.discount) ?? manualDiscount,
      0
    );
    const baselineDiscountSource = sourceBeforePromotion === 'client_category' && effectiveDiscount > 0
      ? 'client_category'
      : effectiveDiscount > 0
        ? 'manual'
        : 'none';
    const manualSubtotal = computeManualSubtotal(item);
    const ordinaryUnitPrice = Math.max((toNumber(item?.price) || 0) - effectiveDiscount, 0);
    const ordinaryResult = {
        ...item,
        manualDiscount,
        discount: effectiveDiscount,
        discountSource: baselineDiscountSource,
        promotionId: null,
        promotionName: null,
        promotionType: null,
        promotionDiscountAmount: 0,
        promotionSnapshot: null,
        subtotal: manualSubtotal,
        orderDiscountEligibleSubtotal: manualSubtotal,
        promotionProtectedSubtotal: 0,
    };

    const promotionOptions = activePromotions
      .map((promotion) => computePromotionPricing(item, promotion, ordinaryUnitPrice))
      .filter(Boolean)
      .sort((left, right) => (
        left.subtotal - right.subtotal
        || comparePromotionIds(left.promotion.id, right.promotion.id)
      ));

    return pruneDominatedLineOptions([withTaxAdjustedOptimizationCoordinates({
      eligibleSubtotal: manualSubtotal,
      protectedSubtotal: 0,
      isPromotion: false,
      choiceId: 0,
      result: ordinaryResult,
    }, taxProfile), ...promotionOptions.map((candidate) => withTaxAdjustedOptimizationCoordinates({
      eligibleSubtotal: candidate.eligibleSubtotal,
      protectedSubtotal: candidate.protectedSubtotal,
      isPromotion: true,
      choiceId: candidate.promotion.id,
      result: {
        ...item,
        manualDiscount,
        discount: 0,
        discountAuthorizationId: manualDiscount > 0
          ? toInteger(item?.discountAuthorizationId)
          : null,
        discountSource: 'promotion',
        promotionId: candidate.promotion.id,
        promotionName: candidate.promotion.name,
        promotionType: candidate.promotion.type,
        promotionDiscountAmount: Math.max(baseSubtotal - candidate.subtotal, 0),
        promotionSnapshot: buildPromotionSnapshot(candidate.promotion, candidate),
        subtotal: candidate.subtotal,
        orderDiscountEligibleSubtotal: candidate.eligibleSubtotal,
        promotionProtectedSubtotal: candidate.protectedSubtotal,
      },
    }, taxProfile))]);
  });

  const selectedOptionIndexes = chooseGloballyCheapestOptions(optionsByLine, pricingOptions);
  return optionsByLine.map((lineOptions, index) => (
    lineOptions[selectedOptionIndexes[index]]?.result || lineOptions[0].result
  ));
};

export const computeLineItemsSubtotal = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const subtotal = toNumber(item?.subtotal);
    if (Number.isFinite(subtotal) && subtotal >= 0) return sum + Math.floor(subtotal);
    return sum + computeManualSubtotal(item);
  }, 0);

export const computeLineItemsNonDiscountableSubtotal = (items = []) => (
  (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const subtotal = toNumber(item?.subtotal);
    const resolvedSubtotal = Number.isFinite(subtotal) && subtotal >= 0
      ? Math.floor(subtotal)
      : computeManualSubtotal(item);
    if (getFormatOfferId(item)) return sum + resolvedSubtotal;
    const eligible = toNumber(item?.orderDiscountEligibleSubtotal);
    if (!Number.isFinite(eligible)) return sum;
    return sum + Math.max(resolvedSubtotal - Math.floor(eligible), 0);
  }, 0)
);

export const computeOrderTotalsFromSubtotal = ({
  subtotal,
  offerSubtotal = 0,
  nonDiscountableSubtotal,
  orderDiscountAmount,
  orderDiscountPercentage,
}) => {
  const resolvedSubtotal = Math.max(Math.floor(Number(subtotal || 0)), 0);
  const protectedSubtotal = nonDiscountableSubtotal == null
    ? Number(offerSubtotal || 0)
    : Number(nonDiscountableSubtotal || 0);
  const eligibleSubtotal = Math.max(resolvedSubtotal - protectedSubtotal, 0);
  const percent = orderDiscountPercentage != null ? Number(orderDiscountPercentage) : null;
  const discountAmount = Number.isFinite(percent)
    ? Math.min(Math.max(Math.floor(eligibleSubtotal * (percent / 100)), 0), eligibleSubtotal)
    : Math.min(resolveOrderDiscountAmount(orderDiscountAmount), eligibleSubtotal);
  return {
    subtotal: resolvedSubtotal,
    total: Math.max(resolvedSubtotal - discountAmount, 0),
    discountAmount,
  };
};
